import { useCallback, useEffect, useRef, useState } from "react";
import { searchOrgUsers } from "@/features/organizations/api";

export default function AddMemberModal({
  orgId,
  onAdd,
  onClose,
}: {
  orgId: string;
  onAdd: (userId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<
    { userId: string; username: string; displayName: string }[]
  >([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    inputRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "Tab") {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  const doSearch = useCallback(
    async (q: string) => {
      if (q.trim().length < 2) {
        setResults([]);
        return;
      }
      setSearching(true);
      const { data } = await searchOrgUsers(orgId, q.trim());
      if (data) setResults(data);
      setSearching(false);
    },
    [orgId],
  );

  function handleChange(value: string) {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.4)]"
      role="dialog"
      aria-modal="true"
      aria-label="Add member"
      ref={dialogRef}
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-[20px] p-5 w-[90%] max-w-md shadow-[1px_1px_0px_0px_#1c1b2e] border border-text flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <span className="text-text text-[1.125rem] font-black">Add Member</span>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-text flex items-center justify-center bg-white cursor-pointer"
          >
            <span className="text-text font-bold">✕</span>
          </button>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Search by name or username..."
          className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
        />

        {searching && <div className="text-text-muted text-sm text-center py-4">Searching...</div>}

        {!searching && results.length === 0 && query.length >= 2 && (
          <div className="text-text-muted text-sm text-center py-4">No users found</div>
        )}

        <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
          {results.map((user) => (
            <button
              key={user.userId}
              type="button"
              onClick={() => {
                onAdd(user.userId);
                onClose();
              }}
              className="flex items-center gap-3 p-3 rounded-[12px] bg-card hover:bg-card-strong cursor-pointer border border-[rgba(28,27,46,0.1)] text-left font-inherit"
            >
              <div className="w-8 h-8 rounded-full bg-[rgba(255,45,126,0.1)] flex items-center justify-center shrink-0">
                <span className="text-aqua text-sm font-extrabold">
                  {user.displayName?.charAt(0)?.toUpperCase() ?? "?"}
                </span>
              </div>
              <div className="flex-1">
                <span className="text-text text-sm font-bold block">{user.displayName}</span>
              </div>
              <span className="text-aqua text-sm font-extrabold">+</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
